import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { confirmAction } from '../components/ui/Confirm';
import type {
  CheckinEntry,
  CheckinPerson,
  CheckinResponse,
  CheckinResult,
  CheckinView,
  DataResponse,
} from '../contracts';

const publicApi = axios.create({ baseURL: import.meta.env.VITE_API_URL ?? '/api', timeout: 15000 });

type Person = CheckinPerson;
type View = CheckinView;
type Result = CheckinResult;

/** A tap waiting in the offline queue; label is for the screen only and is not sent. */
interface Entry extends CheckinEntry {
  day: string;
  at: string;
  label: string;
}

interface Detector {
  detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]>;
}

const torontoToday = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
const newId = () =>
  (crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '')
    : `${Date.now()}${Math.random().toString(36).slice(2)}`
  ).slice(0, 40);

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Check-in at the door through a time-limited link: no login, names, affiliations and
 * statuses only. Works offline: the roster is kept on the device and taps wait in a local
 * queue that syncs when the connection is back (replaying it is harmless).
 */
export default function Checkin() {
  const token = window.location.pathname.split('/checkin/')[1]?.split('/')[0] ?? '';
  const storeKey = `divert-checkin:${token.slice(0, 12)}`;
  const [view, setView] = useState<View | null>(() => load<View | null>(`${storeKey}:view`, null));
  const [queue, setQueue] = useState<Entry[]>(() => load<Entry[]>(`${storeKey}:queue`, []));
  const [closed, setClosed] = useState<string | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [q, setQ] = useState('');
  const [code, setCode] = useState('');
  const [walkIn, setWalkIn] = useState<{ name: string; email: string; affiliation: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const syncing = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    localStorage.setItem(`${storeKey}:queue`, JSON.stringify(queue));
  }, [queue, storeKey]);
  useEffect(() => {
    if (view) localStorage.setItem(`${storeKey}:view`, JSON.stringify(view));
  }, [view, storeKey]);

  const forget = useCallback(() => {
    localStorage.removeItem(`${storeKey}:view`);
    localStorage.removeItem(`${storeKey}:queue`);
    setView(null);
    setQueue([]);
  }, [storeKey]);

  const refresh = useCallback(async () => {
    try {
      const res = await publicApi.get<DataResponse<CheckinView>>(`/checkin/${token}`);
      setView(res.data.data);
      setClosed(null);
    } catch (err) {
      const status = (err as { response?: { status?: number; data?: { error?: string } } }).response?.status;
      if (status && [403, 404, 410].includes(status)) {
        setClosed(
          (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
            'This check-in link does not work.',
        );
        if (status !== 403) forget();
      }
    }
  }, [token, forget]);

  const sync = useCallback(async () => {
    if (syncing.current || !navigator.onLine) return;
    const pending = load<Entry[]>(`${storeKey}:queue`, []);
    if (!pending.length) return;
    syncing.current = true;
    try {
      const res = await publicApi.post<DataResponse<CheckinResponse>>(`/checkin/${token}`, {
        entries: pending.map((e): CheckinEntry => ({
          clientId: e.clientId,
          day: e.day,
          at: e.at,
          inviteeId: e.inviteeId,
          code: e.code,
          walkIn: e.walkIn,
        })),
      });
      const results: Result[] = res.data.data.results;
      const done = new Set(results.map((r) => r.clientId));
      setQueue((current) => current.filter((e) => !done.has(e.clientId)));
      const people = results.filter((r) => r.person).map((r) => r.person as Person);
      if (people.length) {
        setView((v) =>
          v
            ? {
                ...v,
                roster: [...v.roster.filter((p) => !people.some((x) => x.id === p.id)), ...people].sort((a, b) =>
                  a.name.localeCompare(b.name),
                ),
              }
            : v,
        );
      }
      const problems = results.filter((r) => r.status === 'not_found' || r.status === 'rejected');
      if (problems.length) {
        const labels = problems.map((r) => pending.find((e) => e.clientId === r.clientId)?.label ?? 'an entry');
        setNotice(
          `Not checked in: ${labels.join(', ')} (${problems[0].status === 'not_found' ? 'no such code' : problems[0].reason})`,
        );
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status && [403, 404, 410].includes(status))
        setClosed('This check-in link no longer works. Taps not yet synced are kept on this device.');
    } finally {
      syncing.current = false;
    }
  }, [token, storeKey]);

  useEffect(() => {
    refresh();
    const up = () => {
      setOnline(true);
      sync();
    };
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    const timer = setInterval(() => {
      sync();
      if (navigator.onLine) refresh();
    }, 30000);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      clearInterval(timer);
    };
  }, [refresh, sync]);

  const today = torontoToday();
  const isEventDay = Boolean(view?.days.some((d) => d.date === today));
  const pendingIds = useMemo(
    () => new Set(queue.filter((e) => e.day === today && e.inviteeId).map((e) => e.inviteeId)),
    [queue, today],
  );

  const enqueue = (e: Omit<Entry, 'clientId' | 'day' | 'at'>) => {
    const entry: Entry = { ...e, clientId: newId(), day: today, at: new Date().toISOString() };
    setQueue((current) => {
      const next = [...current, entry];
      localStorage.setItem(`${storeKey}:queue`, JSON.stringify(next));
      return next;
    });
    setNotice(`${e.label}: checked in${navigator.onLine ? '' : ' (offline, will sync)'}`);
    setTimeout(sync, 50);
  };

  const results = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!view || !words.length || q.trim().length < 2) return [];
    return view.roster.filter((p) => words.every((w) => p.name.toLowerCase().includes(w))).slice(0, 25);
  }, [q, view]);

  const stopScan = useCallback(() => {
    const stream = videoRef.current?.srcObject as MediaStream | null;
    stream?.getTracks().forEach((t) => t.stop());
    setScanning(false);
  }, []);

  const startScan = async () => {
    const Ctor = (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => Detector })
      .BarcodeDetector;
    if (!Ctor) {
      setNotice('This browser cannot scan codes; type the code printed under the QR instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setScanning(true);
      setTimeout(async () => {
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        const detector = new Ctor({ formats: ['qr_code'] });
        const tick = async () => {
          if (!videoRef.current?.srcObject) return;
          const found = await detector.detect(video).catch(() => []);
          const value = found[0]?.rawValue?.trim();
          if (value) {
            stopScan();
            enqueue({ code: value.toUpperCase(), label: `Code ${value.toUpperCase()}` });
            return;
          }
          setTimeout(tick, 300);
        };
        tick();
      }, 50);
    } catch {
      setNotice('The camera is not available; type the code instead.');
    }
  };

  useEffect(() => stopScan, [stopScan]);

  if (closed && !view) {
    return (
      <div className="min-h-screen bg-paper p-6">
        <div className="bg-white border border-line max-w-[560px] mx-auto p-6 text-[15px]">{closed}</div>
      </div>
    );
  }
  if (!view) return <div className="min-h-screen bg-paper p-6 text-[15px]">Loading… / Chargement…</div>;

  const checkedToday = view.roster.filter((p) => p.checkedIn.includes(today) || pendingIds.has(p.id)).length;

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-surface text-ink border-b border-line px-4 py-3 sticky top-0 z-10">
        <div className="text-small text-ink-3">Check-in · Accueil</div>
        <div className="font-serif font-semibold text-[20px] leading-7">{view.title}</div>
        <div className="text-small mt-1 flex flex-wrap gap-x-3">
          <span>{checkedToday} checked in today</span>
          <span>{online ? 'Online' : 'Offline'}</span>
          {queue.length ? (
            <span className="font-semibold">{queue.length} waiting to sync</span>
          ) : (
            <span>All synced</span>
          )}
        </div>
      </header>
      <main className="p-4 max-w-[640px] mx-auto">
        {closed ? <div className="bg-white border border-warning p-3 mb-3 text-table">{closed}</div> : null}
        {!isEventDay ? (
          <div className="bg-white border border-warning p-3 mb-3 text-table">
            Today is not one of this event’s days.
          </div>
        ) : null}
        {notice ? (
          <div className="bg-white border border-line-strong p-3 mb-3 text-table" onClick={() => setNotice(null)}>
            {notice}
          </div>
        ) : null}
        <input
          className="w-full border border-line-strong bg-white px-4 py-4 text-[17px]"
          placeholder="Search a name / Chercher un nom"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
        />
        <ul className="mt-2">
          {results.map((p) => {
            const done = p.checkedIn.includes(today) || pendingIds.has(p.id);
            return (
              <li key={p.id}>
                <button
                  disabled={done || !isEventDay}
                  onClick={() => {
                    enqueue({ inviteeId: p.id, label: p.name });
                    setQ('');
                  }}
                  className={`w-full text-left bg-white border px-4 py-4 mt-2 ${done ? 'border-accent' : 'border-line active:bg-selected'}`}
                >
                  <div className="text-[17px]">{p.name}</div>
                  <div className="text-table text-ink-2">
                    {p.affiliation ?? ''}
                    {p.affiliation ? ' · ' : ''}
                    {done ? 'checked in' : p.status.replace('_', ' ')}
                  </div>
                </button>
              </li>
            );
          })}
          {q.trim().length >= 2 && !results.length ? (
            <li className="text-table text-ink-2 mt-3">Nobody by that name. Add a walk-in below.</li>
          ) : null}
        </ul>
        <section className="mt-6 bg-white border border-line p-4">
          <div className="text-table font-semibold">Nametag code / Code du porte-nom</div>
          <div className="flex gap-2 mt-2">
            <input
              className="flex-1 border border-line-strong px-3 py-3 text-[17px]"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="AB3D5FGH"
            />
            <button
              className="border border-line-strong px-4 text-table whitespace-nowrap"
              disabled={code.trim().length < 4 || !isEventDay}
              onClick={() => {
                enqueue({ code: code.trim(), label: `Code ${code.trim()}` });
                setCode('');
              }}
            >
              Check in
            </button>
          </div>
          <button
            className="mt-3 border border-line-strong px-4 py-3 text-table w-full"
            disabled={!isEventDay}
            onClick={() => (scanning ? stopScan() : startScan())}
          >
            {scanning ? 'Stop scanning' : 'Scan a nametag'}
          </button>
          {scanning ? <video ref={videoRef} className="w-full mt-3 bg-black" playsInline muted /> : null}
        </section>
        <section className="mt-4 bg-white border border-line p-4">
          {!walkIn ? (
            <button
              className="border border-line-strong px-4 py-3 text-table w-full"
              disabled={!isEventDay}
              onClick={() => setWalkIn({ name: '', email: '', affiliation: '' })}
            >
              Add a walk-in / Ajouter une personne
            </button>
          ) : (
            <div className="space-y-2">
              <input
                className="w-full border border-line-strong px-3 py-3 text-[16px]"
                placeholder="Name / Nom"
                value={walkIn.name}
                onChange={(e) => setWalkIn({ ...walkIn, name: e.target.value })}
              />
              <input
                className="w-full border border-line-strong px-3 py-3 text-[16px]"
                placeholder="Email / Courriel"
                type="email"
                value={walkIn.email}
                onChange={(e) => setWalkIn({ ...walkIn, email: e.target.value })}
                autoCapitalize="none"
              />
              <input
                className="w-full border border-line-strong px-3 py-3 text-[16px]"
                placeholder="Affiliation"
                value={walkIn.affiliation}
                onChange={(e) => setWalkIn({ ...walkIn, affiliation: e.target.value })}
              />
              <div className="flex gap-2">
                <button
                  className="flex-1 rounded-sm bg-primary border border-primary font-semibold px-4 py-3 text-table"
                  disabled={!walkIn.name.trim()}
                  onClick={() => {
                    enqueue({ walkIn, label: walkIn.name });
                    setWalkIn(null);
                  }}
                >
                  Check in
                </button>
                <button className="border border-line px-4 py-3 text-table" onClick={() => setWalkIn(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
        <div className="mt-6 flex gap-3 text-table">
          <button
            className="underline"
            onClick={() => {
              sync();
              refresh();
            }}
          >
            Sync now
          </button>
          <button
            className="underline text-ink-2"
            onClick={() => {
              if (!queue.length) forget();
              else
                void confirmAction('Taps not yet synced will be lost. Clear this device?').then((ok) => ok && forget());
            }}
          >
            Clear this device
          </button>
        </div>
        <p className="text-small text-ink-2 mt-3">
          This page keeps the list on this device so it works without Wi-Fi. Clear the device when the event is over.
        </p>
      </main>
    </div>
  );
}
